import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import usePageTracker from "@/hooks/usePageTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Search, ArrowLeft, Calendar, MapPin, Plane, Clock, CheckCircle, XCircle, AlertCircle, MessageCircle, Phone, User, Navigation, Loader2, PlaneTakeoff, PlaneLanding, Timer } from "lucide-react";

import getApiUrl from "@/utils/api";
import { fmtChicagoTime } from "@/utils/timezone";

const API = getApiUrl();

// Passenger Check-In Component
function PassengerCheckIn({ bookingId, currentStatus, onCheckIn }) {
  const [loading, setLoading] = useState(false);
  const [showDelayInput, setShowDelayInput] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState("");
  const [notes, setNotes] = useState("");

  const handleCheckIn = async (status) => {
    if (status === "delayed" && !delayMinutes) {
      setShowDelayInput(true);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/check-in`, {
        booking_id: bookingId,
        status: status,
        delay_minutes: status === "delayed" ? parseInt(delayMinutes) : null,
        notes: notes || null
      });
      
      toast.success(res.data.message);
      onCheckIn && onCheckIn(res.data);
      setShowDelayInput(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = {
    on_flight: { label: "On Flight", color: "bg-green-100 text-green-800 border-green-300", icon: PlaneTakeoff },
    missed_flight: { label: "Missed Flight", color: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
    delayed: { label: "Delayed", color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: Timer }
  };

  if (currentStatus) {
    const config = statusConfig[currentStatus] || statusConfig.on_flight;
    const Icon = config.icon;
    return (
      <div className={`p-4 rounded-lg border-2 ${config.color}`} data-testid="check-in-status">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" />
          <span className="font-semibold">Checked In: {config.label}</span>
        </div>
        <p className="text-sm mt-1 opacity-80">Your driver has been notified</p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="check-in-form">
      <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
        <PlaneLanding className="w-5 h-5" />
        Flight Check-In
      </h4>
      <p className="text-sm text-blue-700 mb-4">Let your driver know your flight status:</p>
      
      {!showDelayInput ? (
        <div className="grid grid-cols-3 gap-2">
          <Button
            onClick={() => handleCheckIn("on_flight")}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white h-auto py-3 flex-col"
            data-testid="check-in-on-flight"
          >
            <PlaneTakeoff className="w-5 h-5 mb-1" />
            <span className="text-xs">On Flight</span>
          </Button>
          <Button
            onClick={() => handleCheckIn("delayed")}
            disabled={loading}
            className="bg-yellow-500 hover:bg-yellow-600 text-white h-auto py-3 flex-col"
            data-testid="check-in-delayed"
          >
            <Timer className="w-5 h-5 mb-1" />
            <span className="text-xs">Delayed</span>
          </Button>
          <Button
            onClick={() => handleCheckIn("missed_flight")}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white h-auto py-3 flex-col"
            data-testid="check-in-missed"
          >
            <XCircle className="w-5 h-5 mb-1" />
            <span className="text-xs">Missed</span>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label className="text-sm text-blue-900">How many minutes delayed?</Label>
            <Input
              type="number"
              placeholder="e.g., 30"
              value={delayMinutes}
              onChange={(e) => setDelayMinutes(e.target.value)}
              className="mt-1"
              data-testid="delay-minutes-input"
            />
          </div>
          <div>
            <Label className="text-sm text-blue-900">Notes (optional)</Label>
            <Textarea
              placeholder="Any additional info for your driver..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowDelayInput(false)}
              variant="outline"
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={() => handleCheckIn("delayed")}
              disabled={loading || !delayMinutes}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600"
              data-testid="confirm-delay-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Delay"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Driver Location Tracker Component
function DriverLocationTracker({ bookingId, driverName }) {
  const [location, setLocation] = useState(null);
  const [locationShared, setLocationShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchLocation = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/driver-location`);
      if (res.data.location_shared && res.data.current_location) {
        setLocation(res.data.current_location);
        setLocationShared(true);
        setLastUpdated(res.data.current_location.updated_at);
      } else {
        setLocation(null);
        setLocationShared(false);
      }
    } catch (error) {
      console.error("Failed to fetch driver location:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh location every 30 seconds when enabled
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      fetchLocation();
      interval = setInterval(fetchLocation, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, bookingId]);

  const openInWaze = () => {
    if (location) {
      window.open(`https://waze.com/ul?ll=${location.lat},${location.lng}&navigate=yes`, '_blank');
    }
  };

  return (
    <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100" data-testid="driver-location-tracker">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium">Driver Location</span>
          {autoRefresh && (
            <span className="text-xs text-green-600 animate-pulse">Live</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLocation}
            disabled={loading}
            className="h-8"
            data-testid="track-driver-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-1" />
                Track
              </>
            )}
          </Button>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`h-8 ${autoRefresh ? 'bg-green-600 hover:bg-green-700' : ''}`}
            data-testid="auto-refresh-btn"
          >
            {autoRefresh ? "Live On" : "Auto"}
          </Button>
        </div>
      </div>
      
      {locationShared && location && (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-green-600">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              Driver is sharing location
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={openInWaze}
              className="h-8 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              data-testid="open-waze-btn"
            >
              <Navigation className="w-3 h-3 mr-1" />
              Open in Waze
            </Button>
          </div>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {fmtChicagoTime(lastUpdated)} CT
            </p>
          )}
        </div>
      )}
      
      {!locationShared && !loading && (
        <p className="text-xs text-muted-foreground mt-2">
          {driverName}'s location will appear here once they share it
        </p>
      )}
    </div>
  );
}

// Driver information
const DRIVERS = [
  {
    name: "Mike",
    phone: "+18159148139",
    whatsapp: "18159148139",
    role: "Lead Driver"
  },
  {
    name: "Shabba",
    phone: "+13128248601",
    whatsapp: "13128248601",
    role: "Driver"
  },
  {
    name: "X",
    phone: "+16086446067",
    whatsapp: "16086446067",
    role: "Driver"
  }
];

export default function CheckStatus() {
  usePageTracker("Check Status");
  const [searchType, setSearchType] = useState("whatsapp");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [bookings, setBookings] = useState([]);
  const [singleBooking, setSingleBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleWhatsappSearch = async () => {
    if (!whatsappNumber.trim()) {
      toast.error("Please enter your WhatsApp number");
      return;
    }

    setLoading(true);
    setSearched(true);
    setSingleBooking(null);
    
    try {
      const response = await axios.get(`${API}/bookings/check/${encodeURIComponent(whatsappNumber)}`);
      // Handle new response structure with individual_bookings and group_bookings
      const allBookings = [
        ...(response.data.individual_bookings || []),
        ...(response.data.group_bookings || []).map(b => ({
          ...b,
          full_name: b.organizer_name,
          whatsapp_number: b.organizer_whatsapp
        }))
      ];
      setBookings(allBookings);
      
      if (allBookings.length === 0) {
        toast.info("No bookings found for this number");
      }
    } catch (error) {
      console.error("Search failed:", error);
      toast.error("Failed to search bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationSearch = async () => {
    if (!confirmationNumber.trim()) {
      toast.error("Please enter your confirmation number");
      return;
    }

    setLoading(true);
    setSearched(true);
    setBookings([]);
    
    try {
      const response = await axios.get(`${API}/bookings/lookup/confirmation/${encodeURIComponent(confirmationNumber.toUpperCase())}`);
      
      if (response.data.found) {
        // Also fetch the full booking details
        const fullBookingRes = await axios.get(`${API}/bookings/check/${encodeURIComponent(response.data.whatsapp_number)}`);
        // Handle new response structure
        const allBookings = [
          ...(fullBookingRes.data.individual_bookings || []),
          ...(fullBookingRes.data.group_bookings || []).map(b => ({
            ...b,
            full_name: b.organizer_name,
            whatsapp_number: b.organizer_whatsapp
          }))
        ];
        const matchedBooking = allBookings.find(b => 
          b.id.toUpperCase().startsWith(confirmationNumber.toUpperCase()) ||
          confirmationNumber.toUpperCase().startsWith(b.id.slice(0, 8).toUpperCase())
        );
        
        if (matchedBooking) {
          setSingleBooking(matchedBooking);
          toast.success(`Welcome back, ${response.data.full_name}!`);
        } else {
          setSingleBooking({ 
            ...response.data, 
            id: response.data.confirmation_id,
            booking_status: "confirmed",
            payment_status: "paid"
          });
          toast.success(`Found booking for ${response.data.full_name}!`);
        }
      } else {
        setSingleBooking(null);
        toast.error("No booking found with this confirmation number");
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSingleBooking(null);
      toast.error("Confirmation number not found");
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = (driver) => {
    const message = encodeURIComponent(
      `Hi ${driver.name}, this is a passenger from Easy Transportation. My confirmation number is: ${confirmationNumber || singleBooking?.id?.slice(0, 8) || "N/A"}`
    );
    window.open(`https://wa.me/${driver.whatsapp}?text=${message}`, "_blank");
  };

  const getStatusBadge = (status, type) => {
    const styles = {
      booking: {
        pending: { variant: "secondary", icon: AlertCircle, text: "Pending Confirmation" },
        confirmed: { variant: "default", icon: CheckCircle, text: "Confirmed" },
        cancelled: { variant: "destructive", icon: XCircle, text: "Cancelled" }
      },
      payment: {
        pending: { variant: "secondary", icon: AlertCircle, text: "Payment Pending" },
        pending_verification: { variant: "outline", icon: Clock, text: "Verifying Payment" },
        paid: { variant: "default", icon: CheckCircle, text: "Paid" }
      }
    };

    const config = styles[type]?.[status] || styles[type]?.pending;
    if (!config) return null;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const renderBookingCard = (booking) => (
    <Card 
      key={booking.id} 
      className="shadow-lg border-0 rounded-3xl overflow-hidden"
      data-testid={`booking-card-${booking.id}`}
    >
      <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {booking.trip_type === "arrival" ? "Airport Pickup" : "Return Trip"}
            </CardTitle>
            <p className="text-xs text-muted-foreground font-mono mt-1">
              Confirmation: <span className="font-bold text-primary">{booking.id?.slice(0, 8)?.toUpperCase() || booking.confirmation_id}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {getStatusBadge(booking.booking_status, "booking")}
            {getStatusBadge(booking.payment_status, "payment")}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Travel Date & Time</p>
                <p className="font-semibold">{booking.travel_date} at {booking.travel_time || "TBD"}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Route</p>
                <p className="font-semibold">{booking.pickup_location}</p>
                <p className="text-sm text-muted-foreground">to</p>
                <p className="font-semibold">{booking.dropoff_location || booking.housing_unit}</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Plane className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Flight Info</p>
                <p className="font-semibold">{booking.airline || "N/A"} - {booking.flight_number || "N/A"}</p>
                {(booking.arrival_terminal || booking.arrival_gate) && (
                  <p className="text-sm text-blue-600 mt-0.5">
                    {[
                      booking.arrival_terminal ? `Terminal ${booking.arrival_terminal}` : '',
                      booking.arrival_gate ? `Gate ${booking.arrival_gate}` : ''
                    ].filter(Boolean).join(' | ')}
                  </p>
                )}
                {booking.flight_status && (
                  <Badge className={`mt-1 text-xs ${
                    booking.flight_status === 'landed' ? 'bg-green-100 text-green-700' :
                    booking.flight_status === 'active' ? 'bg-blue-100 text-blue-700' :
                    booking.flight_status === 'delayed' ? 'bg-red-100 text-red-700' :
                    booking.flight_status === 'scheduled' ? 'bg-gray-100 text-gray-600' :
                    booking.flight_status === 'cancelled' ? 'bg-red-200 text-red-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    <Plane className="w-3 h-3 mr-1" />
                    {booking.flight_status === 'landed' ? 'Landed' :
                     booking.flight_status === 'active' ? 'In Air' :
                     booking.flight_status === 'delayed' ? 'Delayed' :
                     booking.flight_status === 'scheduled' ? 'On Time' :
                     booking.flight_status === 'cancelled' ? 'Cancelled' :
                     booking.flight_status.charAt(0).toUpperCase() + booking.flight_status.slice(1)}
                    {booking.landing_time ? ` ${booking.landing_time}` : ''}
                  </Badge>
                )}
                {(booking.delay_minutes || 0) > 0 && (
                  <p className="text-xs text-red-600 font-medium mt-0.5">+{booking.delay_minutes} min delay</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Passenger</p>
                <p className="font-semibold">{booking.full_name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Driver Section */}
        {booking.assigned_driver_name && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
              <User className="w-5 h-5" />
              Your Assigned Driver
            </h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {booking.assigned_driver_name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-lg">{booking.assigned_driver_name}</p>
                  <p className="text-sm text-blue-600">{booking.assigned_driver_phone}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.open(`https://wa.me/${booking.assigned_driver_whatsapp}?text=${encodeURIComponent(`Hi ${booking.assigned_driver_name}, this is ${booking.full_name}. My confirmation: ${booking.id?.slice(0,8)?.toUpperCase()}`)}`, '_blank')}
                  className="bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <a href={`tel:${booking.assigned_driver_phone}`}>
                  <Button variant="outline" className="rounded-lg">
                    <Phone className="w-4 h-4 mr-2" />
                    Call
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Driver Location Tracking */}
            <DriverLocationTracker bookingId={booking.id} driverName={booking.assigned_driver_name} />
          </div>
        )}

        {/* Passenger Check-In Section */}
        {booking.trip_type === "arrival" && (booking.booking_status === "confirmed" || booking.booking_status === "pending") && (
          <div className="mt-6">
            <PassengerCheckIn 
              bookingId={booking.id} 
              currentStatus={booking.passenger_status}
              onCheckIn={(data) => {
                // Update the booking status locally
                booking.passenger_status = data.status;
              }}
            />
          </div>
        )}

        {/* Connect with Driver Section - Show all drivers if no assigned driver */}
        {!booking.assigned_driver_name && (booking.booking_status === "confirmed" || booking.booking_status === "pending") && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Connect with Your Driver via WhatsApp
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {DRIVERS.map((driver) => (
                <Button
                  key={driver.name}
                  onClick={() => openWhatsApp(driver)}
                  className="bg-green-600 hover:bg-green-700 rounded-xl h-auto py-3"
                  data-testid={`whatsapp-${driver.name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold">{driver.name}</p>
                      <p className="text-xs opacity-80">{driver.phone}</p>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
            <p className="text-xs text-green-700 mt-3 text-center">
              Click to open WhatsApp and message your driver directly
            </p>
          </div>
        )}

        {booking.payment_status === "pending_verification" && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <p className="text-yellow-800 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              We're verifying your payment. This usually takes a few hours.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="check-status-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Check Booking & Connect
          </h1>
          <p className="text-muted-foreground mt-2">
            Find your booking and connect with your driver via WhatsApp
          </p>
        </div>

        {/* Search Tabs */}
        <Card className="shadow-xl border-0 rounded-3xl mb-8">
          <CardContent className="p-6">
            <Tabs value={searchType} onValueChange={setSearchType}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="confirmation" className="rounded-xl">
                  By Confirmation #
                </TabsTrigger>
                <TabsTrigger value="whatsapp" className="rounded-xl">
                  By WhatsApp
                </TabsTrigger>
              </TabsList>

              <TabsContent value="confirmation">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-gray-600 mb-2 block">Confirmation Number</Label>
                    <Input
                      value={confirmationNumber}
                      onChange={(e) => setConfirmationNumber(e.target.value.toUpperCase())}
                      placeholder="e.g., ABC12345"
                      data-testid="confirmation-search-input"
                      className="h-12 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-primary uppercase"
                      onKeyDown={(e) => e.key === "Enter" && handleConfirmationSearch()}
                    />
                  </div>
                  <Button
                    onClick={handleConfirmationSearch}
                    disabled={loading}
                    data-testid="search-confirmation-btn"
                    className="h-12 px-8 rounded-full bg-green-600 hover:bg-green-700 text-white self-end"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Searching...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Find Booking
                      </span>
                    )}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="whatsapp">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-gray-600 mb-2 block">WhatsApp Number</Label>
                    <Input
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      placeholder="+1 234 567 8900"
                      data-testid="whatsapp-search-input"
                      className="h-12 rounded-xl bg-gray-50 border-transparent focus:bg-white focus:border-primary"
                      onKeyDown={(e) => e.key === "Enter" && handleWhatsappSearch()}
                    />
                  </div>
                  <Button
                    onClick={handleWhatsappSearch}
                    disabled={loading}
                    data-testid="search-btn"
                    className="h-12 px-8 rounded-full bg-primary text-primary-foreground self-end"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Searching...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        Search
                      </span>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Quick Contact Drivers */}
        <Card className="shadow-lg border-0 rounded-3xl mb-8 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Phone className="w-5 h-5 text-green-600" />
              Contact Drivers Directly
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DRIVERS.map((driver) => (
                <a 
                  key={driver.name}
                  href={`https://wa.me/${driver.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold">{driver.name}</p>
                    <p className="text-sm text-green-600">{driver.phone}</p>
                    <p className="text-xs text-muted-foreground">{driver.role}</p>
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && (
          <div className="space-y-6" data-testid="booking-results">
            {/* Single booking from confirmation search */}
            {singleBooking && renderBookingCard(singleBooking)}

            {/* Multiple bookings from WhatsApp search */}
            {bookings.length > 0 && bookings.map((booking) => renderBookingCard(booking))}

            {/* No results */}
            {!singleBooking && bookings.length === 0 && (
              <Card className="shadow-lg border-0 rounded-3xl">
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    No Bookings Found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    We couldn't find any bookings. Please check your confirmation number or WhatsApp number.
                  </p>
                  <Link to="/book">
                    <Button className="rounded-full bg-primary text-primary-foreground">
                      Book a Trip Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
